import _ from 'lodash';
import RequestError from './RequestError';
import api from '@/api';
import { FALLBACK_CATALOG, normalizeCatalog } from '@/components/Captioning/captionLanguages';
import { normalizeLimits, normalizeUsage } from '@/components/Captioning/captionLimits';
import { normalizeSettingsSummary } from '@/components/Captioning/captionRecognition';
import { normalizeExportInfo, normalizeSession } from '@/components/Captioning/captionExport';
import {
	ENGINE_SYNCWORDS,
	ENGINE_CASTR,
	FALLBACK_ENGINE_INFO,
	normalizeEngineInfo,
	normalizeIngest,
	normalizeSyncWordsBlock,
	normalizeSyncWordsStatus,
	normalizeProvider,
	normalizeProviderUsage,
	normalizeListProvider,
	syncwordsErrorMessage,
} from '@/components/Captioning/captionSyncWords';

/**
 * Live captioning flows. Core proxies every call to the captions worker; the
 * dashboard never sees the worker or its token.
 *
 * Contract (core):
 *   GET    /captions                 -> { flows: [flow], limits, usage }
 *   POST   /captions { streamKey }   -> { flow }              409 if the stream already has a flow
 *   GET    /captions/:id             -> { flow, status }
 *   PUT    /captions/:id { source, langs, delaySec, translations? } -> { flow, status, restarted }
 *          400 code 'captions_invalid_translations', errors: [{ path: 'translations.fr.instructions', message }]
 *   GET    /captions/:id/status      -> status
 *   POST   /captions/:id/start { source, langs, delaySec, durationSec } -> { flow, session, status, serverNow }
 *   POST   /captions/:id/stop        -> { flow, session, status, serverNow }
 *   POST   /captions/:id/extend { durationSec } -> same; 409 code 'window_closed'
 *   GET    /captions/languages       -> { languages, hub, maxOutputs, sourceTrackOptional?, profanitySources?, nonEnglishPairs? }
 *   DELETE /captions/:id             -> { success: true }
 *   PUT    /captions/:id/recognition { settings } -> { flow, status, serverNow, dedupedCount, appliesAt }
 *          400 code 'captions_invalid_settings', errors: [{ path, message }] (top level and under data)
 *   POST   /captions/:id/recognition/apply -> same as GET /captions/:id; 409 'window_not_running'
 *   GET    /captions/:id/sessions?limit=20 -> { sessions }   (see captionExport.js)
 *   GET    /captions/:id/export      -> { streamKey, languages, configured, formats, maxSpanSec }
 *   GET    /captions/:id/export/<lang>.<ext>?from&to&t0 -> caption file (blob)
 *
 * Flows carry `translations` (lang -> { engine, formality?, compress?,
 * genderAware?, instructions? }, see captionTranslations.js); absent reads as {}.
 * Flows carry `settings` (recognition block, see captionRecognition.js) and,
 * in the list, `settingsSummary: { vocabCount, replacementsCount, hasSettings }`.
 * An older core sends neither; they normalize to `{}` and zeros.
 *
 * Every flow response also carries plan `limits` and `usage` (see
 * captionLimits.js). An older core sends neither; `planFrom()` then yields the
 * defaults, which render the pre-limits UI unchanged. Limit errors are Boom
 * payloads `{ statusCode, message, data: { code, ... } }` with codes
 * `captions_not_included` (403), `captions_too_many_languages`,
 * `captions_concurrency_limit`, `captions_pool_exhausted`,
 * `captions_window_too_long` (409) and `captions_rate_limited` (429); the
 * thrown RequestError exposes them as `code` and `data`.
 *
 * Flows carry `engine` ('castr' | 'syncwords'). SyncWords flows (see
 * captionSyncWords.js) also carry `ingest` and a `syncwords` settings block,
 * take `{ syncwords }` on create/update/start, and their status adds
 * `engineState` and `syncwords`. `GET /captions/engine` says which engine new
 * flows get.
 */
export default {
	listFlows,
	createFlow,
	getEngine,
	getEngineForStream,
	resetEngineCache,
	getProvider,
	connectProvider,
	disconnectProvider,
	getFlow,
	updateFlow,
	getStatus,
	startFlow,
	stopFlow,
	extendFlow,
	deleteFlow,
	updateRecognition,
	applyRecognition,
	getSessions,
	getExportInfo,
	downloadExport,
	getLanguages,
	resetLanguagesCache,
	embedUrlFor,
	cachedList,
	cachedFlow,
	forgetFlow,
};

/**
 * Last successful list for this SPA session. The dashboard paints it
 * immediately on return, and the detail page seeds from a flow in it so
 * opening a card does not wait on `GET /captions/:id`. A full reload starts
 * empty. Callers must not mutate `flows` in place.
 */
let listCache = null;

function cachedList() {
	return listCache;
}

function cachedFlow(flowId) {
	if (!listCache || flowId == null || flowId === '') return null;
	const id = String(flowId);
	return listCache.flows.find((flow) => String(flow.id) === id) || null;
}

function rememberList(result) {
	listCache = {
		flows: result.flows.slice(),
		limits: result.limits,
		usage: result.usage,
		hasLimits: result.hasLimits,
		provider: result.provider ?? null,
	};
}

function rememberFlow(flow) {
	if (!flow || flow.id == null) return;
	if (!listCache) {
		listCache = { flows: [flow], ...planFrom(null) };
		return;
	}
	const id = String(flow.id);
	const without = listCache.flows.filter((item) => String(item.id) !== id);
	listCache = { ...listCache, flows: [flow, ...without] };
}

function forgetFlow(flowId) {
	if (!listCache || flowId == null) return;
	const id = String(flowId);
	listCache = {
		...listCache,
		flows: listCache.flows.filter((flow) => String(flow.id) !== id),
	};
}

/** `{ flows, limits, usage }`; an older core answers a bare array (no limits). */
async function listFlows() {
	const res = await makeRequest({ path: '/captions', method: 'get' });
	const flows = Array.isArray(res?.flows) ? res.flows : Array.isArray(res) ? res : [];
	// `provider`: the account's own SyncWords connection and its usage this month (null on an older core).
	const result = { flows: flows.map(normalizeFlow), ...planFrom(res), provider: normalizeListProvider(res?.provider) };
	rememberList(result);
	return result;
}

/**
 * Normalized `{ limits, usage, hasLimits }` from a response envelope.
 * `hasLimits` says whether core actually sent them, so a caller can keep the
 * numbers it already has when a particular response omits them.
 */
function planFrom(res) {
	const hasLimits = !!res && typeof res === 'object' && !!res.limits && typeof res.limits === 'object';
	const limits = normalizeLimits(hasLimits ? res.limits : null);
	// Own-account flows: the linked account's usage this month, on the envelope or in limits/usage.
	const rawProviderUsage = res?.providerUsage ?? res?.usage?.providerUsage ?? res?.limits?.providerUsage;
	return {
		limits,
		usage: normalizeUsage(hasLimits ? res.usage : null, limits),
		hasLimits,
		providerUsage: normalizeProviderUsage(rawProviderUsage),
	};
}

let languagesPromise = null;

/**
 * `GET /captions/languages` -> { languages, hub, maxOutputs, sourceTrackOptional,
 * profanitySources, nonEnglishPairs } (the last three absent on an older core). The catalog is
 * static per deploy, so it is fetched once per session; a failure (older core
 * without the endpoint, or the service down) resolves to the local fallback
 * so the picker still works, and is retried on the next call.
 */
function getLanguages() {
	if (languagesPromise) return languagesPromise;
	languagesPromise = makeRequest({ path: '/captions/languages', method: 'get' })
		.then((res) => normalizeCatalog(res))
		.catch(() => {
			languagesPromise = null;
			return FALLBACK_CATALOG;
		});
	return languagesPromise;
}

function resetLanguagesCache() {
	languagesPromise = null;
}

let enginePromise = null;

/**
 * `GET /captions/engine` -> { engine, syncwords } (captionSyncWords.js). Fetched
 * once per session like the language catalog; a failure (older core without
 * the endpoint) resolves to the castr engine with the documented SyncWords
 * defaults, and is retried on the next call.
 */
function getEngine() {
	if (enginePromise) return enginePromise;
	enginePromise = makeRequest({ path: '/captions/engine', method: 'get' })
		.then((res) => normalizeEngineInfo(res))
		.catch(() => {
			enginePromise = null;
			const fallback = normalizeEngineInfo(FALLBACK_ENGINE_INFO);
			return { ...fallback, syncwords: { ...fallback.syncwords, listsKnown: false } };
		});
	return enginePromise;
}

/**
 * `GET /captions/engine?streamKey=` for a picked stream: the same block plus
 * `stream.region`, the region core picks for it when the create sends none.
 * Not cached; 404/403 for a stream that isn't the caller's.
 */
async function getEngineForStream(streamKey) {
	const res = await makeRequest({ path: '/captions/engine', method: 'get', params: { streamKey } });
	return normalizeEngineInfo(res);
}

function resetEngineCache() {
	enginePromise = null;
}

/**
 * The account's own SyncWords connection (captionSyncWords.js `normalizeProvider`).
 * Connecting or disconnecting changes what `GET /captions/engine` says, so
 * both drop its cache. Keys are sent once and never kept here.
 */
async function getProvider() {
	return normalizeProvider(await makeRequest({ path: '/captions/provider', method: 'get' }));
}

/** 400 captions_provider_invalid, 422 captions_provider_rejected, 502 _unreachable, 503 _unavailable. */
async function connectProvider({ apiKey, apiSecret }) {
	const res = await makeRequest({ path: '/captions/provider', method: 'put', data: { apiKey, apiSecret } });
	resetEngineCache();
	return normalizeProvider(res);
}

/** 409 captions_provider_in_use with `runningFlows: [{ id, streamName }]` while own-account captions run. */
async function disconnectProvider() {
	const res = await makeRequest({ path: '/captions/provider', method: 'delete' });
	resetEngineCache();
	return normalizeProvider(res);
}

/** `syncwords` (settings object) only for a SyncWords flow; core picks the engine. */
async function createFlow(streamKey, { syncwords } = {}) {
	const data = { streamKey };
	if (syncwords && typeof syncwords === 'object') data.syncwords = syncwords;
	const res = await makeRequest({ path: '/captions', method: 'post', data });
	const flow = normalizeFlow(res?.flow ?? res);
	rememberFlow(flow);
	return flow;
}

/**
 * `{ flow, status, statusError }`. Core answers 200 with `status: null` and
 * `statusError: 'unreachable'` when the captions service is down; the flow is
 * still editable in that state.
 */
async function getFlow(flowId) {
	const res = await makeRequest({ path: `/captions/${flowId}`, method: 'get' });
	return {
		flow: normalizeFlow(res?.flow ?? res),
		status: withServerNow(res?.status ?? null, res),
		statusError: typeof res?.statusError === 'string' ? res.statusError : null,
		session: res?.session ?? null,
		window: res?.window ?? null,
		serverNow: Number.isFinite(Number(res?.serverNow)) ? Number(res.serverNow) : null,
		...planFrom(res),
	};
}

/**
 * `translations` is the complete map, sent only when the caller includes it.
 * A SyncWords flow sends `{ syncwords }` alone (409 `captions_settings_locked`
 * while running).
 */
async function updateFlow(flowId, { source, langs, delaySec, translations, syncwords }) {
	let data;
	if (syncwords !== undefined) {
		data = { syncwords };
	} else {
		data = { source, langs, delaySec };
		if (translations !== undefined) data.translations = translations;
	}
	const res = await makeRequest({
		path: `/captions/${flowId}`,
		method: 'put',
		data,
	});
	return {
		flow: normalizeFlow(res?.flow ?? res),
		status: withServerNow(res?.status ?? null, res),
		restarted: !!res?.restarted,
		serverNow: Number.isFinite(Number(res?.serverNow)) ? Number(res.serverNow) : null,
		...planFrom(res),
	};
}

async function getStatus(flowId) {
	return normalizeSyncWordsStatus(await makeRequest({ path: `/captions/${flowId}/status`, method: 'get' }));
}

/**
 * Opens a caption window with the given settings. `durationSec` is one of the
 * presets or null for unlimited. A SyncWords flow passes `syncwords`: the
 * changed settings to apply first, or null to start with the saved ones.
 */
async function startFlow(flowId, { source, langs, delaySec, durationSec, syncwords }) {
	const data = syncwords !== undefined
		? { durationSec: durationSec ?? null, ...(syncwords ? { syncwords } : {}) }
		: { source, langs, delaySec, durationSec: durationSec ?? null };
	const res = await makeRequest({
		path: `/captions/${flowId}/start`,
		method: 'post',
		data,
	});
	return sessionResult(res);
}

async function stopFlow(flowId) {
	const res = await makeRequest({ path: `/captions/${flowId}/stop`, method: 'post' });
	return sessionResult(res);
}

/** 409 `{ code: 'window_closed' }` when the window already ended. */
async function extendFlow(flowId, durationSec) {
	const res = await makeRequest({ path: `/captions/${flowId}/extend`, method: 'post', data: { durationSec } });
	return sessionResult(res);
}

function sessionResult(res) {
	return {
		flow: normalizeFlow(res?.flow ?? res),
		session: res?.session ?? null,
		status: withServerNow(res?.status ?? null, res),
		serverNow: Number.isFinite(Number(res?.serverNow)) ? Number(res.serverNow) : null,
		...planFrom(res),
	};
}

/**
 * Core puts `serverNow` and `window` either on the status or on the
 * envelope; fold envelope copies onto the status so callers read one object.
 */
function withServerNow(status, envelope) {
	if (!status || typeof status !== 'object') return status;
	const out = normalizeSyncWordsStatus({ ...status });
	if (out.serverNow === undefined && Number.isFinite(Number(envelope?.serverNow))) out.serverNow = Number(envelope.serverNow);
	if (out.window === undefined && envelope?.window && typeof envelope.window === 'object') out.window = envelope.window;
	return out;
}

async function deleteFlow(flowId) {
	const res = await makeRequest({ path: `/captions/${flowId}`, method: 'delete' });
	forgetFlow(flowId);
	return res;
}

/**
 * Saves the complete recognition block. `appliesAt` is 'within_seconds' when
 * a running window picks it up live, 'next_start' otherwise.
 */
async function updateRecognition(flowId, settings) {
	const res = await makeRequest({ path: `/captions/${flowId}/recognition`, method: 'put', data: { settings } });
	return {
		flow: normalizeFlow(res?.flow ?? res),
		status: withServerNow(res?.status ?? null, res),
		serverNow: Number.isFinite(Number(res?.serverNow)) ? Number(res.serverNow) : null,
		dedupedCount: Number.isFinite(Number(res?.dedupedCount)) ? Number(res.dedupedCount) : 0,
		appliesAt: res?.appliesAt === 'within_seconds' ? 'within_seconds' : 'next_start',
		...planFrom(res),
	};
}

/** Restarts a running window so saved settings apply now; 409 `window_not_running` otherwise. */
async function applyRecognition(flowId) {
	const res = await makeRequest({ path: `/captions/${flowId}/recognition/apply`, method: 'post' });
	return {
		flow: normalizeFlow(res?.flow ?? res),
		status: withServerNow(res?.status ?? null, res),
		statusError: typeof res?.statusError === 'string' ? res.statusError : null,
		session: res?.session ?? null,
		window: res?.window ?? null,
		serverNow: Number.isFinite(Number(res?.serverNow)) ? Number(res.serverNow) : null,
		...planFrom(res),
	};
}

/** Recent caption sessions, newest first as core sends them. `limit` is 1–100. 404 on an older core. */
async function getSessions(flowId, limit = 20) {
	const n = Math.min(100, Math.max(1, Math.round(Number(limit)) || 20));
	const res = await makeRequest({ path: `/captions/${flowId}/sessions`, method: 'get', params: { limit: n } });
	const list = Array.isArray(res?.sessions) ? res.sessions : [];
	return list.map(normalizeSession).filter(Boolean);
}

/**
 * What can be exported for a flow. 404 on an older core; 404 with
 * `captions_export_not_found` when nothing was ever recorded for the stream.
 */
async function getExportInfo(flowId) {
	const res = await makeRequest({ path: `/captions/${flowId}/export`, method: 'get' });
	return normalizeExportInfo(res);
}

/**
 * One caption file as a blob, with the export headers core sends.
 * `segments`/`cues` are null when the headers are missing (or not exposed
 * to the browser by CORS); the caller then treats the file as non-empty.
 */
async function downloadExport(flowId, { lang, ext, from, to, t0 }) {
	const iso = (ms) => new Date(ms).toISOString();
	let res;
	try {
		res = await api.request({
			url: `/captions/${flowId}/export/${encodeURIComponent(lang)}.${ext}`,
			method: 'get',
			params: { from: iso(from), to: iso(to), t0: iso(t0) },
			responseType: 'blob',
		});
	} catch (err) {
		// A blob request gets its error body as a blob too; read it as JSON.
		const body = _.get(err, 'response.data');
		if (typeof Blob !== 'undefined' && body instanceof Blob) {
			try {
				err.response.data = JSON.parse(await body.text());
			} catch {
				err.response.data = null;
			}
		}
		throw toRequestError(err);
	}
	const headers = res?.headers || {};
	const header = (name) => (typeof headers.get === 'function' ? headers.get(name) : headers[name]) ?? null;
	const count = (name) => {
		const v = header(name);
		return v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v);
	};
	return {
		blob: res.data,
		segments: count('x-captions-export-segments'),
		cues: count('x-captions-export-cues'),
		disposition: header('content-disposition') || '',
	};
}

/**
 * Public player URL for a stream key, matching how the rest of the app builds
 * embed links (StreamSidebarPlayback, DynamicEmbed).
 */
function embedUrlFor(streamKey) {
	const base = process.env.VUE_APP_PLAYER_APP_BASE_URL || '';
	if (!streamKey) return '';
	return `${base.replace(/\/$/, '')}/${streamKey}`;
}

/**
 * Fills the fields the UI relies on so a partial payload from core never
 * blanks a view: `langs` is always an array, `playback.embed` is derived when
 * core omits it.
 */
function normalizeFlow(raw) {
	if (!raw || typeof raw !== 'object') return raw;
	const playback = raw.playback && typeof raw.playback === 'object' ? { ...raw.playback } : {};
	if (!playback.embed) playback.embed = embedUrlFor(raw.streamKey);
	return {
		...raw,
		source: raw.source ?? null,
		langs: Array.isArray(raw.langs) ? raw.langs : [],
		delaySec: Number.isFinite(Number(raw.delaySec)) ? Number(raw.delaySec) : 0,
		configured: !!raw.configured,
		streamDeleted: raw.streamDeleted === true,
		// Kept as-is: `undefined` (older core, no summaries) is meaningful to the
		// list, which then falls back to a per-flow status call.
		summary: raw.summary,
		settings: raw.settings && typeof raw.settings === 'object' && !Array.isArray(raw.settings) ? raw.settings : {},
		settingsSummary: normalizeSettingsSummary(raw.settingsSummary),
		translations: raw.translations && typeof raw.translations === 'object' && !Array.isArray(raw.translations) ? raw.translations : {},
		playback,
		...normalizeEngineFields(raw),
	};
}

/** `engine` on every flow; `ingest` and `syncwords` for a SyncWords flow (null otherwise). */
function normalizeEngineFields(raw) {
	if (raw.engine !== ENGINE_SYNCWORDS) return { engine: ENGINE_CASTR, ingest: null, syncwords: null };
	return { engine: ENGINE_SYNCWORDS, ingest: normalizeIngest(raw.ingest), syncwords: normalizeSyncWordsBlock(raw.syncwords) };
}

async function makeRequest(reqConfig) {
	reqConfig.url = reqConfig.path;

	let res;
	try {
		res = await api.request(reqConfig);
	} catch (err) {
		throw toRequestError(err);
	}

	return res && res.data;
}

/** A RequestError carrying the HTTP status, core's `code`, Boom `data` and field `errors`. */
function toRequestError(err) {
	const status = _.get(err, 'response.status');
	const edata = _.get(err, 'response.data');
	const requestError = new RequestError(
		edata && typeof edata === 'object'
			? { ...edata, statusCode: edata.statusCode ?? status }
			: { statusCode: status, message: typeof edata === 'string' && edata ? edata : undefined },
	);
	// HTTP status is what the UI branches on (400 validation, 409 conflict,
	// 502 captions service unreachable); keep it even when the body has none.
	requestError.status = status ?? null;
	// Machine-readable code core attaches to some errors: top-level for
	// 'window_closed', under Boom `data` for the plan-limit codes.
	const edataObj = edata && typeof edata === 'object' ? edata : null;
	const boomData = edataObj && edataObj.data && typeof edataObj.data === 'object' ? edataObj.data : null;
	requestError.code = typeof edataObj?.code === 'string'
		? edataObj.code
		: typeof boomData?.code === 'string' ? boomData.code : null;
	// The numbers a limit error itemises ({ maxLangs }, { remainingSec, periodEnd }, ...).
	requestError.data = boomData;
	// The running flows a 409 captions_provider_in_use names, on the body or under `data`.
	const runningFlows = Array.isArray(edataObj?.runningFlows) ? edataObj.runningFlows : boomData?.runningFlows;
	if (Array.isArray(runningFlows)) requestError.runningFlows = runningFlows.filter((f) => f && f.id != null).map((f) => ({ id: String(f.id), streamName: f.streamName || '' }));
	// Field errors of a 400 `captions_invalid_settings` / `captions_invalid_translations`, on the body or under `data`.
	requestError.errors = Array.isArray(edataObj?.errors)
		? edataObj.errors
		: Array.isArray(boomData?.errors) ? boomData.errors : null;
	const syncwordsMessage = syncwordsErrorMessage(requestError.code, requestError.message);
	if (syncwordsMessage) requestError.message = syncwordsMessage;
	if (!requestError.message) {
		requestError.message = status >= 500 || !status
			? 'The captioning service isn\'t reachable right now. Try again.'
			: 'The request could not be completed.';
	}
	return requestError;
}
